import React, {useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {Box, Button, Card, Grid} from '@mui/material'
import {getSpacing, PGTheme, PGTypography} from '@progynyglobal/pg-ui-library'
import {enqueueSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import {useCreateAccountMutation, useMeQuery} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {FERTILITY, MENOPAUSE} from '@progyny/utils/constants'

const useStyles = makeStyles()(() => ({
  journeyBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    border: `2px solid ${PGTheme.common.lightGray}`,
    padding: 24,
    width: '100%',
    borderRadius: 16,
    marginBottom: 20,
    cursor: 'pointer',
    color: PGTheme.common.black,
  },
  selectedJourney: {
    border: `2px solid ${PGTheme.productLines.fertility.primary}`,
    backgroundColor: PGTheme.productLines.fertility.primaryLight,
    color: PGTheme.productLines.fertility.primary,
  },
}))

const SelectJourney = () => {
  const {classes} = useStyles()
  const {data, refetch} = useMeQuery()
  const [selectedJourney, setSelectedJourney] = useState(
    data?.me?.member?.activeMemberJourney || ''
  )
  const {t} = useTranslation()
  const getErrorMessage = useErrorCodes()
  const [createAccount] = useCreateAccountMutation()
  const history = useHistory()
  const {getPath} = useRoutes()

  const handleSelectJourney = (journey: string) => {
    setSelectedJourney(journey)
    createAccount({
      variables: {
        memberJourney: journey,
      },
    })
      .then(({data}) => {
        if (data && data.createAccount) {
          if (data.createAccount.success) {
            refetch()
            history.push(getPath(RouteEnum.PERSONALIZE_JOURNEY))
          } else if (data?.createAccount?.errors) {
            const errors = data?.createAccount?.errors
            const message = errors[0]?.messages[0]
            return enqueueSnackbar({
              message: getErrorMessage(message as string),
              variant: 'error',
            })
          } else {
            const errorCode = data.createAccount.errorCode
            enqueueSnackbar({
              message: getErrorMessage(errorCode as string),
              variant: 'error',
            })
          }
        }
      })
      .catch((err: any) => {
        console.error(err)
        enqueueSnackbar({
          message: t('profile_bank_details_cannot_reach_server'),
          variant: 'error',
        })
        return
      })
  }

  return (
    <Grid
      container
      direction={'column'}
      justifyContent={'center'}
      sx={{padding: {xs: 2, md: 8}}}
    >
      <Grid item xs={12} md={9}>
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <Card
            sx={{
              padding: {xs: 2, md: 8},
              width: {xs: '100%', md: '700px'},
              borderRadius: {xs: 0, md: '32px'},
              boxShadow: {xs: 'none', md: 1},
            }}
          >
            <Box mb={4} textAlign='center'>
              <PGTypography variant='header'>
                <Trans i18nKey='create_account_select_journey_how_can_we_support_you' />
              </PGTypography>
              <PGTypography variant='body'>
                <Trans i18nKey='create_account_select_journey_your_answers_help_us_tailor' />
              </PGTypography>
            </Box>
            <Box>
              {data?.me?.member?.availableProductLines?.map((productLine) => (
                <Button
                  key={productLine}
                  className={`${classes.journeyBlock} ${selectedJourney === productLine ? classes.selectedJourney : ''}`}
                  onClick={() => handleSelectJourney(productLine!)}
                >
                  <PGTypography variant='label'>
                    <Trans>
                      {productLine === FERTILITY
                        ? 'Fertility & Family Forming'
                        : productLine === MENOPAUSE
                          ? 'Menopause'
                          : 'Pregnancy & Postpartum'}
                    </Trans>
                  </PGTypography>
                  <PGTypography variant='caption'>
                    {productLine === FERTILITY
                      ? t('select_journey_tailor_care')
                      : productLine === MENOPAUSE
                        ? t('select_journey_support_symptoms')
                        : t('select_journey_support_pregnancy')}
                  </PGTypography>
                </Button>
              ))}
            </Box>
          </Card>
        </Box>
      </Grid>
    </Grid>
  )
}

export default SelectJourney
